import { adoptStylesheets } from '@theme/section-styles';
import { standardEvents as standardEventsPromise } from '@theme/standard-events-bridge';

(() => {
  'use strict';

  if (window.__dieuxProductLinkSelectorRuntime) return;
  window.__dieuxProductLinkSelectorRuntime = true;

  const ownerSelector = 'product-link-selector[data-secondary-product-options]';
  const linkSelector = '[data-secondary-product-option]';
  const productOwnerSelector =
    'product-component[data-product-grid-content][data-template-product-state-owner][view-event-payload]';
  const responseCacheLimit = 6;
  const responseCacheLifetime = 60_000;
  const pointerPrefetchDelay = 150;
  const maxConcurrentPrefetches = 3;
  const viewportWarmMargin = '150% 0px';
  const responseCache = new Map();
  const parsedProductPages = new WeakMap();
  const prefetchControllers = new Map();
  const prefetchTimers = new Map();
  let navigationController = null;
  let navigationCacheKey = null;
  let navigationIntent = 0;
  let commitInProgress = false;
  let hasCommittedNavigation = false;
  let lastCommittedPageUrl = window.location.href;

  function pathForUrl(url) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function requestUrlFor(url) {
    const requestUrl = new URL(url, window.location.href);
    requestUrl.hash = '';
    return requestUrl;
  }

  function normalizedPath(pathname) {
    return pathname.replace(/\/+$/, '') || '/';
  }

  function destinationForLink(link) {
    try {
      const destination = new URL(
        link.getAttribute('href') || link.href,
        window.location.href
      );
      if (
        destination.origin !== window.location.origin ||
        !['http:', 'https:'].includes(destination.protocol) ||
        destination.username ||
        destination.password
      ) {
        return null;
      }

      return destination;
    } catch {
      return null;
    }
  }

  function isCurrentDestination(link, destination) {
    if (link.hasAttribute('aria-current')) return true;

    const current = new URL(window.location.href);
    return (
      normalizedPath(destination.pathname) === normalizedPath(current.pathname) &&
      destination.search === current.search &&
      destination.hash === current.hash
    );
  }

  function isSameDocumentFragmentEntry() {
    let committed;
    try {
      committed = new URL(lastCommittedPageUrl || window.location.href);
    } catch {
      return false;
    }

    return (
      window.location.origin === committed.origin &&
      normalizedPath(window.location.pathname) === normalizedPath(committed.pathname) &&
      Boolean(window.location.hash)
    );
  }

  function canUseLink(link) {
    return !(
      link.hasAttribute('download') ||
      link.target && link.target !== '_self' ||
      link.relList.contains('external')
    );
  }

  function canSpeculate(owner, link, { requireSettled = true } = {}) {
    if (
      !owner.isConnected ||
      !owner.ajaxEnabled ||
      !owner.prefetchEnabled ||
      !canUseLink(link) ||
      link.hasAttribute('aria-current') ||
      link.getClientRects().length === 0 ||
      document.visibilityState !== 'visible' ||
      navigator.connection?.saveData ||
      navigator.connection?.effectiveType?.includes('2g') ||
      requireSettled && document.readyState !== 'complete'
    ) {
      return false;
    }

    const destination = destinationForLink(link);
    return Boolean(destination && !isCurrentDestination(link, destination));
  }

  function readCachedResponse(cacheKey) {
    const entry = responseCache.get(cacheKey);
    if (!entry) return null;

    if (!entry.settled || Date.now() - entry.cachedAt <= responseCacheLifetime) {
      responseCache.delete(cacheKey);
      responseCache.set(cacheKey, entry);
      return entry.promise;
    }

    deleteCachedResponse(cacheKey, { abort: true });
    return null;
  }

  function deleteCachedResponse(cacheKey, { abort = false } = {}) {
    const entry = responseCache.get(cacheKey);
    if (abort) entry?.controller?.abort();
    responseCache.delete(cacheKey);
    prefetchControllers.delete(cacheKey);
  }

  function trimResponseCache(preservedKey) {
    for (const cacheKey of responseCache.keys()) {
      if (responseCache.size <= responseCacheLimit) return;
      if (cacheKey === preservedKey || cacheKey === navigationCacheKey) continue;
      deleteCachedResponse(cacheKey, { abort: true });
    }
  }

  function requestPage(url, requestKind) {
    const requestUrl = requestUrlFor(url);
    const cacheKey = requestUrl.href;
    const cachedResponse = readCachedResponse(cacheKey);
    if (cachedResponse) return cachedResponse;

    const controller = new AbortController();
    const entry = {
      cachedAt: Date.now(),
      controller,
      promise: null,
      settled: false,
    };

    if (requestKind === 'navigation') {
      navigationController = controller;
      navigationCacheKey = cacheKey;
    } else {
      prefetchControllers.set(cacheKey, controller);
    }

    const request = fetch(requestUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
      priority: requestKind === 'prefetch' ? 'low' : 'high',
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Linked product request failed: ${response.status}`);
        }

        const responseUrl = new URL(response.url, window.location.href);
        if (responseUrl.origin !== window.location.origin) {
          throw new Error('Linked product response changed origin.');
        }

        return response.text().then((html) => ({
          html,
          responseUrl: responseUrl.href,
        }));
      })
      .then((responseData) => {
        entry.cachedAt = Date.now();
        entry.settled = true;
        try {
          productPageFor(responseData);
        } catch {}
        return responseData;
      })
      .catch((error) => {
        if (responseCache.get(cacheKey) === entry) {
          deleteCachedResponse(cacheKey);
        }
        throw error;
      })
      .finally(() => {
        if (prefetchControllers.get(cacheKey) === controller) {
          prefetchControllers.delete(cacheKey);
        }
        if (navigationController === controller) {
          navigationController = null;
          navigationCacheKey = null;
        }
      });

    entry.promise = request;
    responseCache.set(cacheKey, entry);
    trimResponseCache(cacheKey);
    return request;
  }

  function prefetch(owner, link, { highPriority = false } = {}) {
    if (!canSpeculate(owner, link, { requireSettled: !highPriority })) {
      return Promise.resolve(null);
    }

    const destination = destinationForLink(link);
    if (!destination) return Promise.resolve(null);

    const cacheKey = requestUrlFor(destination).href;
    const cachedResponse = readCachedResponse(cacheKey);
    if (cachedResponse) return cachedResponse;
    if (prefetchControllers.size >= maxConcurrentPrefetches) return Promise.resolve(null);

    return requestPage(destination, highPriority ? 'navigation-intent' : 'prefetch')
      .catch(() => null);
  }

  function cancelPrefetchTimer(link) {
    const timer = prefetchTimers.get(link);
    if (timer) window.clearTimeout(timer);
    prefetchTimers.delete(link);
  }

  function schedulePrefetch(owner, link, delay = pointerPrefetchDelay) {
    cancelPrefetchTimer(link);
    if (!canSpeculate(owner, link)) return;

    const timer = window.setTimeout(() => {
      prefetchTimers.delete(link);
      prefetch(owner, link).catch(() => {});
    }, delay);
    prefetchTimers.set(link, timer);
  }

  function clearOwnerPrefetchTimers(owner) {
    owner.querySelectorAll(linkSelector).forEach((link) => cancelPrefetchTimer(link));
  }

  function abortSpeculativeRequests() {
    prefetchTimers.forEach((timer) => window.clearTimeout(timer));
    prefetchTimers.clear();
    for (const cacheKey of Array.from(prefetchControllers.keys())) {
      deleteCachedResponse(cacheKey, { abort: true });
    }
  }

  function parseProductDocument(responseData) {
    const parsedDocument = new DOMParser().parseFromString(responseData.html, 'text/html');
    const nextMain = parsedDocument.getElementById('MainContent');
    const pageTemplate = nextMain?.dataset.template || '';
    const nextProductOwner = nextMain?.querySelector(productOwnerSelector);
    const nextSelector = nextMain?.querySelector(ownerSelector);
    const currentLinks = nextSelector
      ? Array.from(nextSelector.querySelectorAll(`${linkSelector}[aria-current]`))
      : [];
    const selectedLink = currentLinks[0] || null;
    let productPayload = null;

    try {
      productPayload = JSON.parse(nextProductOwner?.getAttribute('view-event-payload') || '');
    } catch {}

    if (
      !nextMain ||
      !pageTemplate.startsWith('product') ||
      !nextProductOwner ||
      !nextSelector ||
      currentLinks.length !== 1 ||
      !selectedLink ||
      !productPayload ||
      typeof productPayload !== 'object' ||
      Array.isArray(productPayload)
    ) {
      throw new Error('Linked product response did not contain the requested product page.');
    }

    return {
      documentTitle: parsedDocument.title,
      nextMain,
      pageTemplate,
      parsedDocument,
      productId:
        nextSelector.dataset.productId ||
        selectedLink.dataset.clickProductid ||
        '',
      productPayload,
      responseUrl: new URL(responseData.responseUrl, window.location.href),
      selectedUrl: destinationForLink(selectedLink),
    };
  }

  function productPageFor(responseData) {
    let parse = parsedProductPages.get(responseData);
    if (!parse) {
      try {
        parse = { page: parseProductDocument(responseData) };
      } catch (error) {
        parse = { error };
      }
      parsedProductPages.set(responseData, parse);
    }

    if (parse.error) throw parse.error;
    return parse.page;
  }

  function parseProductResponse(responseData, destination, expectedProductId = '') {
    const page = productPageFor(responseData);

    if (
      page.responseUrl.origin !== window.location.origin ||
      !page.selectedUrl ||
      normalizedPath(page.selectedUrl.pathname) !== normalizedPath(destination.pathname) ||
      expectedProductId && String(page.productId) !== String(expectedProductId)
    ) {
      throw new Error('Linked product response identity did not match the requested product.');
    }

    const committedUrl = new URL(page.responseUrl.href);
    committedUrl.hash = destination.hash;

    return {
      committedUrl,
      documentTitle: page.documentTitle,
      nextMain: page.nextMain,
      pageTemplate: page.pageTemplate,
      parsedDocument: page.parsedDocument,
      productId: page.productId,
      productPayload: page.productPayload,
    };
  }

  function syncMainAttributes(currentMain, nextMain) {
    for (const attribute of Array.from(currentMain.attributes)) {
      if (attribute.name !== 'id' && !nextMain.hasAttribute(attribute.name)) {
        currentMain.removeAttribute(attribute.name);
      }
    }

    for (const attribute of Array.from(nextMain.attributes)) {
      if (attribute.name !== 'id') {
        currentMain.setAttribute(attribute.name, attribute.value);
      }
    }
  }

  function syncMetadata(parsedDocument) {
    const selectors = [
      'link[rel="canonical"]',
      'meta[name="description"]',
      'meta[name="robots"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]',
      'meta[property^="product:"]',
    ];

    document.title = parsedDocument.title;
    for (const selector of selectors) {
      document.head.querySelectorAll(selector).forEach((element) => element.remove());
      parsedDocument.head.querySelectorAll(selector).forEach((element) => {
        document.head.append(document.importNode(element, true));
      });
    }
  }

  function externalModulesToLoad(nextMain) {
    const loadedSources = new Set(
      Array.from(document.querySelectorAll('script[type="module"][src]'))
        .map((script) => script.src)
    );

    return Array.from(nextMain.querySelectorAll('script[type="module"][src]'))
      .filter((script) => {
        const source = new URL(script.src, window.location.href).href;
        if (loadedSources.has(source)) return false;
        loadedSources.add(source);
        return true;
      })
      .map((script) => ({
        crossOrigin: script.crossOrigin,
        fetchPriority: script.fetchPriority,
        integrity: script.integrity,
        referrerPolicy: script.referrerPolicy,
        src: new URL(script.src, window.location.href).href,
      }));
  }

  function activateInlineModules(root) {
    for (const script of root.querySelectorAll('script[type="module"]:not([src])')) {
      const replacement = document.createElement('script');
      replacement.type = 'module';
      replacement.textContent = script.textContent;
      script.replaceWith(replacement);
    }
  }

  function loadExternalModules(modules) {
    for (const moduleData of modules) {
      if (
        Array.from(document.querySelectorAll('script[type="module"][src]'))
          .some((script) => script.src === moduleData.src && script.closest('head'))
      ) {
        continue;
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.src = moduleData.src;
      script.fetchPriority = moduleData.fetchPriority || 'low';
      if (moduleData.crossOrigin) script.crossOrigin = moduleData.crossOrigin;
      if (moduleData.integrity) script.integrity = moduleData.integrity;
      if (moduleData.referrerPolicy) script.referrerPolicy = moduleData.referrerPolicy;
      document.head.append(script);
    }
  }

  function dispatchVirtualPageView(data, navigation) {
    try {
      document.dispatchEvent(
        new CustomEvent('dieux:virtual-page-view', {
          detail: {
            dieuxVirtualPageView: true,
            page: {
              template: data.pageTemplate,
              title: data.documentTitle,
              url: navigation.committedUrl.href,
            },
            pageReferrer: navigation.previousPageUrl,
          },
        })
      );
    } catch {
      // Analytics must never block a committed linked-product navigation.
    }
  }

  function dispatchStandardViews(data, navigation) {
    const pagePayload = {
      page: {
        template: data.pageTemplate,
        title: data.documentTitle,
        url: navigation.committedUrl.href,
      },
    };

    standardEventsPromise.then((standardEvents) => {
      if (!standardEvents) return;

      try {
        document.dispatchEvent(new standardEvents.PageViewEvent(pagePayload));
      } catch {
        // Analytics interoperability must never block a committed linked-product navigation.
      }
    });
  }

  function setBusy(owner, busy) {
    owner?.toggleAttribute('data-secondary-product-loading', busy);
    const main = document.getElementById('MainContent');
    if (!main) return;

    if (busy) main.setAttribute('aria-busy', 'true');
    else main.removeAttribute('aria-busy');
  }

  function nextPaintedFrame() {
    return new Promise((resolve) => {
      window.DieuxFrame.afterPaint(resolve);
    });
  }

  async function commitProductPage(data, { historyMode, previousPageUrl, restoreFocus }) {
    const currentMain = document.getElementById('MainContent');
    if (!currentMain) {
      throw new Error('Current product page has no main content owner.');
    }

    const externalModules = externalModulesToLoad(data.nextMain);
    const incomingChildren = Array.from(data.nextMain.childNodes)
      .map((node) => document.importNode(node, true));
    const state = {
      ...(history.state || {}),
      dieuxProductSelectorProductId: data.productId,
      dieuxProductSelectorUrl: pathForUrl(data.committedUrl),
    };

    const mutate = () => {
      commitInProgress = true;
      try {
        if (historyMode === 'push') {
          history.pushState(state, '', pathForUrl(data.committedUrl));
        } else if (historyMode === 'replace') {
          history.replaceState(state, '', pathForUrl(data.committedUrl));
        }

        syncMainAttributes(currentMain, data.nextMain);
        currentMain.replaceChildren(...incomingChildren);
        syncMetadata(data.parsedDocument);
      } finally {
        commitInProgress = false;
      }
    };

    const reducedMotion = (window.DieuxFrame?.motionQuery
      ?? window.matchMedia('(prefers-reduced-motion: reduce)')).matches;
    if (typeof document.startViewTransition === 'function' && !reducedMotion) {
      await document.startViewTransition(mutate).updateCallbackDone;
    } else {
      mutate();
    }

    const productOwner = currentMain.querySelector(productOwnerSelector);
    const selectedLink = currentMain.querySelector(
      `${ownerSelector} ${linkSelector}[aria-current]`
    );
    if (!productOwner || !selectedLink) {
      throw new Error('Committed product page lost its product state owner.');
    }

    loadExternalModules(externalModules);
    activateInlineModules(currentMain);
    currentMain.removeAttribute('aria-busy');
    if (restoreFocus) selectedLink.focus({ preventScroll: true });

    lastCommittedPageUrl = data.committedUrl.href;
    hasCommittedNavigation = true;
    const navigation = {
      committedUrl: data.committedUrl,
      previousPageUrl,
    };
    dispatchVirtualPageView(data, navigation);
    dispatchStandardViews(data, navigation);
  }

  async function navigate(
    owner,
    destination,
    {
      expectedProductId = '',
      fallbackUrl = destination.href,
      historyMode = 'push',
      restoreFocus = false,
    } = {}
  ) {
    const intent = ++navigationIntent;
    const cacheKey = requestUrlFor(destination).href;
    const previousPageUrl = lastCommittedPageUrl || window.location.href;

    if (navigationCacheKey !== cacheKey) {
      navigationController?.abort();
      if (navigationCacheKey) deleteCachedResponse(navigationCacheKey);
      navigationController = null;
      navigationCacheKey = null;
    }

    const prefetchedController = prefetchControllers.get(cacheKey);
    if (prefetchedController) {
      prefetchControllers.delete(cacheKey);
      navigationController = prefetchedController;
      navigationCacheKey = cacheKey;
    }

    setBusy(owner, true);

    try {
      const pageRequest = requestPage(destination, 'navigation');
      pageRequest.catch(() => {});

      await nextPaintedFrame();
      if (intent !== navigationIntent) return;

      const responseData = await pageRequest;
      if (intent !== navigationIntent) return;

      const data = parseProductResponse(responseData, destination, expectedProductId);
      if (intent !== navigationIntent) return;

      await adoptStylesheets(data.nextMain);
      if (intent !== navigationIntent) return;

      await commitProductPage(data, {
        historyMode,
        previousPageUrl,
        restoreFocus,
      });
    } catch (error) {
      if (intent !== navigationIntent || error.name === 'AbortError') return;

      setBusy(owner, false);
      window.setTimeout(() => window.location.assign(fallbackUrl), 0);
    }
  }

  function seedHistory(owner) {
    if (commitInProgress || !owner.isConnected) return;

    const currentUrl = new URL(window.location.href);
    const path = pathForUrl(currentUrl);
    if (
      history.state?.dieuxProductSelectorUrl === path &&
      String(history.state?.dieuxProductSelectorProductId || '') ===
        String(owner.dataset.productId || '')
    ) {
      lastCommittedPageUrl = currentUrl.href;
      return;
    }

    history.replaceState(
      {
        ...(history.state || {}),
        dieuxProductSelectorProductId: owner.dataset.productId || '',
        dieuxProductSelectorUrl: path,
      },
      '',
      currentUrl.href
    );
    lastCommittedPageUrl = currentUrl.href;
  }

  class ProductLinkSelector extends HTMLElement {
    connectedCallback() {
      this.lifecycleController?.abort();
      this.lifecycleController = new AbortController();
      const { signal } = this.lifecycleController;

      this.addEventListener('click', this.onClick, { signal });
      this.addEventListener('pointerdown', this.onPointerDown, { passive: true, signal });
      this.addEventListener('pointerover', this.onPointerOver, { passive: true, signal });
      this.addEventListener('pointerout', this.onPointerOut, { passive: true, signal });
      this.addEventListener('focusin', this.onFocusIn, { signal });
      this.addEventListener('focusout', this.onFocusOut, { signal });
      window.addEventListener('load', this.onPrefetchReadinessChange, {
        once: true,
        signal,
      });
      document.addEventListener('visibilitychange', this.onPrefetchReadinessChange, {
        signal,
      });

      seedHistory(this);
      this.observeViewportWarmth(signal);
      this.preloadSiblings();
      this.revealCurrentOption();
    }

    disconnectedCallback() {
      this.lifecycleController?.abort();
      window.clearTimeout(this.siblingsRefreshTimer);
      this.siblingsRefreshTimer = undefined;
      this.siblingsWarming = false;
      this.siblingsInViewport = false;
      clearOwnerPrefetchTimers(this);
    }

    revealCurrentOption() {
      const rail = this.querySelector('dieux-tab-rail');
      const currentLink = this.querySelector(`${linkSelector}[aria-current]`);
      if (!rail || !currentLink) return;

      customElements.whenDefined('dieux-tab-rail').then(() => {
        if (!this.isConnected || !this.contains(currentLink)) return;
        rail.scrollElementIntoView?.(currentLink, { behavior: 'auto' });
      });
    }

    get ajaxEnabled() {
      return (
        !this.hasAttribute('data-secondary-product-ajax-disabled') &&
        !window.Shopify?.designMode
      );
    }

    get prefetchEnabled() {
      return !this.hasAttribute('data-secondary-product-prefetch-disabled');
    }

    linkForEvent(event) {
      const link = event.target instanceof Element
        ? event.target.closest(linkSelector)
        : null;
      return link && this.contains(link) ? link : null;
    }

    onClick = (event) => {
      const link = this.linkForEvent(event);
      if (
        !link ||
        !this.ajaxEnabled ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        !canUseLink(link)
      ) {
        return;
      }

      const destination = destinationForLink(link);
      if (!destination) return;

      event.preventDefault();
      cancelPrefetchTimer(link);
      if (isCurrentDestination(link, destination)) return;

      navigate(this, destination, {
        expectedProductId: link.dataset.clickProductid || '',
        fallbackUrl: link.href,
        historyMode: 'push',
        restoreFocus: event.detail === 0,
      });
    };

    onPointerDown = (event) => {
      const link = this.linkForEvent(event);
      if (
        !link ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      cancelPrefetchTimer(link);
      prefetch(this, link, { highPriority: true }).catch(() => {});
    };

    onPointerOver = (event) => {
      const link = this.linkForEvent(event);
      if (!link || link.contains(event.relatedTarget)) return;
      schedulePrefetch(this, link);
    };

    onPointerOut = (event) => {
      const link = this.linkForEvent(event);
      if (link && !link.contains(event.relatedTarget)) cancelPrefetchTimer(link);
    };

    onFocusIn = (event) => {
      const link = this.linkForEvent(event);
      if (link) schedulePrefetch(this, link, 0);
    };

    onFocusOut = (event) => {
      const link = this.linkForEvent(event);
      if (link) cancelPrefetchTimer(link);
    };

    preloadSiblings = () => {
      if (
        this.siblingsWarming ||
        document.readyState !== 'complete' ||
        document.visibilityState !== 'visible' ||
        !this.ajaxEnabled ||
        !this.prefetchEnabled
      ) {
        return;
      }
      this.siblingsWarming = true;

      const warm = async () => {
        try {
          const links = Array.from(
            this.querySelectorAll(`${linkSelector}:not([aria-current])`)
          );
          await Promise.all(
            links.map((link) =>
              this.isConnected ? prefetch(this, link).catch(() => null) : null
            )
          );
        } finally {
          this.siblingsWarming = false;
          this.scheduleSiblingRefresh();
        }
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => warm(), { timeout: 2000 });
      } else {
        window.setTimeout(warm, 300);
      }
    };

    scheduleSiblingRefresh = () => {
      window.clearTimeout(this.siblingsRefreshTimer);
      this.siblingsRefreshTimer = undefined;
      if (!this.isConnected || !this.siblingsInViewport) return;

      this.siblingsRefreshTimer = window.setTimeout(
        () => this.preloadSiblings(),
        Math.max(responseCacheLifetime - pointerPrefetchDelay * 10, 1000)
      );
    };

    observeViewportWarmth = (signal) => {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          this.siblingsInViewport = entries.some((entry) => entry.isIntersecting);
          if (this.siblingsInViewport) this.preloadSiblings();
          else {
            window.clearTimeout(this.siblingsRefreshTimer);
            this.siblingsRefreshTimer = undefined;
          }
        },
        { rootMargin: viewportWarmMargin }
      );

      observer.observe(this);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    };

    onPrefetchReadinessChange = () => {
      if (document.visibilityState !== 'visible') {
        abortSpeculativeRequests();
        return;
      }
      if (document.readyState !== 'complete') return;

      this.preloadSiblings();

      const focusedLink = document.activeElement?.closest?.(linkSelector);
      if (focusedLink && this.contains(focusedLink)) {
        schedulePrefetch(this, focusedLink, 0);
      }

      const hoveredLink = this.querySelector(`${linkSelector}:hover`);
      if (hoveredLink && hoveredLink !== focusedLink) {
        schedulePrefetch(this, hoveredLink);
      }
    };
  }

  window.addEventListener('popstate', (event) => {
    if (commitInProgress) return;

    const ownsHistoryEntry = Boolean(event.state?.dieuxProductSelectorUrl);
    const owner = document.querySelector(ownerSelector);
    if (!ownsHistoryEntry) {
      if (isSameDocumentFragmentEntry()) return;
      if (hasCommittedNavigation && owner) window.location.reload();
      return;
    }

    if (!(owner instanceof ProductLinkSelector) || !owner.ajaxEnabled) {
      window.location.reload();
      return;
    }

    let destination;
    try {
      destination = new URL(window.location.href);
    } catch {
      window.location.reload();
      return;
    }

    const restoreFocus = owner.contains(document.activeElement);
    navigate(owner, destination, {
      expectedProductId: event.state?.dieuxProductSelectorProductId || '',
      fallbackUrl: destination.href,
      historyMode: 'none',
      restoreFocus,
    });
  });

  document.addEventListener('dieux:product-variant-change', () => {
    lastCommittedPageUrl = window.location.href;
  });

  if (!customElements.get('product-link-selector')) {
    customElements.define('product-link-selector', ProductLinkSelector);
  }
})();
